import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { UserIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { User } from '../types/auth';
import NotificationBell from './NotificationBell';
import HelpButton from './HelpButton';
import Button from './ui/Button';

interface HeaderProps {
  user: User;
  /** Opens the mobile nav drawer (shown only below the lg breakpoint). */
  onOpenNav?: () => void;
}

const Header: React.FC<HeaderProps> = ({ user, onOpenNav }) => {
  const { logout } = useAuth();

  const getPageTitle = () => {
    switch (user.role) {
      case 'admin':
        return 'Admin Dashboard';
      case 'member':
        return 'Clinician Dashboard';
      case 'patient':
        return 'Client Dashboard';
      default:
        return 'Dashboard';
    }
  };

  return (
    <header className="bg-white shadow-clinic-sm border-b border-clinic-primary-lighter">
      <div className="flex items-center justify-between h-20 px-6">
        {/* Page Title */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onOpenNav}
            aria-label="Open navigation menu"
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 -ml-2 rounded-lg text-clinic-dark hover:bg-clinic-surface-sunken focus:outline-none focus:ring-2 focus:ring-clinic-primary transition-colors"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <h2 className="text-xl font-semibold text-clinic-dark font-body">
            {getPageTitle()}
          </h2>
        </div>

        {/* Right side actions */}
        <div className="flex items-center space-x-4">
          {/* Help: "what are you trying to do?" guides */}
          <HelpButton user={user} />

          {/* Notification Bell - only for members (clinicians) */}
          {user.role === 'member' ? (
            <NotificationBell />
          ) : (
            <button className="p-2 text-clinic-muted hover:text-clinic-primary rounded-full hover:bg-clinic-primary-lighter transition-colors">
              <span className="sr-only">Notifications</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
                />
              </svg>
            </button>
          )}

          {/* User Profile - clickable to navigate to profile */}
          <div className="flex items-center space-x-3">
            <div
              className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => {
                const profileRoute = user.role === 'admin' ? '/admin' : user.role === 'member' ? '/member/profile' : '/patient';
                window.location.href = profileRoute;
              }}
              title="View profile"
            >
              {/* Avatar */}
              {user.profile_photo_url ? (
                <img
                  src={user.profile_photo_url}
                  alt={user.full_name}
                  className="w-9 h-9 rounded-full object-cover ring-2 ring-clinic-primary-lighter"
                />
              ) : (
                <div className="w-9 h-9 bg-clinic-primary rounded-full flex items-center justify-center ring-2 ring-clinic-primary-light">
                  <UserIcon className="h-5 w-5 text-clinic-cream" />
                </div>
              )}

              {/* User Info */}
              <div className="text-sm hidden sm:block">
                <p className="font-medium text-clinic-dark font-body">{user.full_name}</p>
                <p className="text-clinic-muted text-xs capitalize">{user.role}</p>
              </div>
            </div>

            {/* Logout Button */}
            <Button
              variant="secondary"
              size="sm"
              onClick={logout}
            >
              Logout
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
